/**
 * Text conditions for plain-text fields (the campaign email subject, the share texts):
 *
 *   {% if FirstName %}Hi #FirstName#{% elseif Country is "NO" %}Hei{% else %}Hello{% endif %}
 *
 * The panel's mirror of the server grammar (api/helpers/TemplateConditions.js: parseText,
 * validateText, buildText): the same tags are ours, the same words make a condition, the same
 * problems are reported in the same order and words. Every delimiter, keyword, quote, operator
 * word and limit is read from the condition spec the server serves at
 * GET /api/v1/variable/condition-spec. No Angular here; the DOM only to decode character
 * references, as the server does.
 */

export interface ConditionOperator {
  id: string;
  label: string;
  needsValue: boolean;
  numeric?: boolean;
  valueTypes?: string[];
  help?: string;
  /** The words written after the variable in a text condition, e.g. `is more than`. */
  syntax?: string;
}

export interface ConditionChoice {
  id: string;
  label: string;
}

export interface TextSyntaxSpec {
  open: string;
  close: string;
  keywords: {
    if: string;
    elseif: string;
    else: string;
    endif: string;
    and: string;
    or: string;
    not: string;
  };
  /** Each way to quote a value: its opening then its closing character, e.g. `“”`. */
  quotes?: string[];
  maxTags: number;
  maxDepth: number;
  maxExpression: number;
  help?: Record<string, string>;
}

export interface ConditionSpec {
  attribute?: string;
  actions?: ConditionChoice[];
  matchModes: ConditionChoice[];
  maxRules: number;
  maxValueLength: number;
  variablePattern: string;
  numberPattern: string;
  operators: ConditionOperator[];
  help?: Record<string, string>;
  text?: TextSyntaxSpec;
  defaults?: Record<string, unknown>;
}

export interface TextRule {
  variable: string;
  operator: string;
  value?: string;
}

export interface TextCondition {
  /** A match mode id from the spec: the first joins rules with `and`, the second with `or`. */
  match: string;
  rules: TextRule[];
}

/** One `if` / `elseif` branch: the text it shows when its condition holds. */
export interface TextCase {
  condition: TextCondition;
  text: string;
}

/** What a block says: its cases in order, then the `else` text (null when there is no else). */
export interface TextBlockContent {
  cases: TextCase[];
  otherwise: string | null;
}

export type TextTagKind = 'if' | 'elseif' | 'else' | 'endif';

export interface TextBranch {
  /** Null for `else`, and for a branch whose condition cannot be read. */
  condition: TextCondition | null;
  /** The branch's tag, `{%` to `%}` (end exclusive). */
  tagStart: number;
  tagEnd: number;
  /** Its text, up to the next tag of the block. */
  contentStart: number;
  contentEnd: number;
}

export interface TextBlock {
  start: number;
  end: number;
  /** 1 for a block in the field's own text, 2 for one inside it, and so on. */
  depth: number;
  branches: TextBranch[];
  /** True once its `endif` is read. */
  closed: boolean;
  /** Why the render removes it (malformed, never closed, too deep); null when it is fine. */
  error: string | null;
}

export interface TextProblem {
  message: string;
  /** The tag the message is about, as written. */
  tag: string;
  /** Where that tag is in the text. */
  start: number;
  end: number;
}

export interface TextScan {
  /** Every block, in the order their `if` appears (outer blocks before the blocks inside them). */
  blocks: TextBlock[];
  /** What the server's validateText reports, in its order. */
  problems: TextProblem[];
  /**
   * What the render removes outside every block: a tag with no `if` before it, and the rest of
   * the text from a tag it reads no further than (never closed, or one tag too many).
   */
  removed: Array<{ start: number; end: number }>;
}

export interface TextScanOptions {
  /** Simple keys (without #) the render knows; omitted = variables are not checked. */
  knownVariables?: Iterable<string> | null;
}

interface Token {
  quoted: boolean;
  text: string;
}

interface Tag {
  kind: TextTagKind;
  start: number;
  /** Null when the tag never closes. */
  end: number | null;
  expression?: string;
  reason?: string;
}

interface ItemError {
  source: string;
  reason: string;
  start: number;
  end: number;
}

interface Item {
  start: number;
  end: number;
  error: ItemError | null;
  block?: TextBlock;
  branches?: Array<{ condition: TextCondition | null; children: Item[] }>;
  source?: string;
  sourceStart?: number;
  sourceEnd?: number;
  hasElse?: boolean;
}

const SPACE = /\s/;
const TAG_KEYWORD = /^[A-Za-z]+/;
// A word another template language ends a branch with, or a misspelling of ours.
const MISSPELT_BRANCH = /^(el|end)/i;
const ZERO_WIDTH = /[​-‍⁠﻿]/g;
// A #Variable# inside a compared value (the engine's TOKEN_PATTERN).
const TOKEN_IN_VALUE = /#[A-Za-z0-9]+#/;
// Used only when an older spec carries no `quotes`.
const FALLBACK_QUOTES = ['""', '\'\'', '“”', '‘’'];

export const quote = (text: string): string => `“${text}”`;

const lastOf = <T>(list: T[]): T => list[list.length - 1];

/** True when the spec carries the text syntax (an older server does not). */
export function hasTextSyntax(spec: ConditionSpec | null | undefined): boolean {
  const text = spec && spec.text;
  return !!(text && text.open && text.close && text.keywords && text.keywords.if && text.keywords.endif
    && Array.isArray(spec.operators) && spec.operators.length && spec.operators.every((operator) => !!operator.syntax));
}

function regex(source: string): RegExp | null {
  if (!source) {
    return null;
  }
  try {
    return new RegExp(source);
  } catch {
    return null;
  }
}

function quotesOf(spec: ConditionSpec): string[] {
  const quotes = spec.text && Array.isArray(spec.text.quotes) ? spec.text.quotes : [];
  return (quotes.length ? quotes : FALLBACK_QUOTES).filter((pair) => typeof pair === 'string' && pair.length === 2);
}

function quoteClose(spec: ConditionSpec): Map<string, string> {
  return new Map(quotesOf(spec).map((pair) => [pair[0], pair[1]] as [string, string]));
}

function operatorById(spec: ConditionSpec, id: string): ConditionOperator | null {
  return (spec.operators || []).find((operator) => operator.id === id) || null;
}

/** The operators that take no value: the first is a bare `Name`, the second `not Name`. */
function shorthandOperators(spec: ConditionSpec): { set: string; empty: string } {
  const plain = (spec.operators || []).filter((operator) => !operator.needsValue);
  return { set: plain[0] ? plain[0].id : '', empty: plain[1] ? plain[1].id : '' };
}

function matchIds(spec: ConditionSpec): { all: string; any: string } {
  const modes = (spec.matchModes || []).map((mode) => mode.id);
  return { all: modes[0] || 'all', any: modes[1] || 'any' };
}

function tagText(spec: ConditionSpec, words: string): string {
  return `${spec.text.open} ${words} ${spec.text.close}`;
}

let decoder: HTMLTemplateElement | null = null;

/**
 * Character references decoded as the server decodes a tag's words before reading them: the way
 * an attribute value is, so a bare `&word` before a letter or digit stays as written ("&notably"
 * is not "¬ably"). An inert template: nothing in it loads or runs.
 */
function decodeEntities(text: string): string {
  if (!text.includes('&') || typeof document === 'undefined') {
    return text;
  }
  decoder = decoder || document.createElement('template');
  decoder.innerHTML = `<i title="${text.replace(/"/g, '&quot;')}"></i>`;
  const element = decoder.content.firstElementChild;
  return element ? element.getAttribute('title') ?? text : text;
}

// The words and quoted values of a condition, in order.
function expressionTokens(expression: string, spec: ConditionSpec): { tokens?: Token[]; error?: string } {
  const close = quoteClose(spec);
  const tokens: Token[] = [];
  let index = 0;
  while (index < expression.length) {
    if (SPACE.test(expression[index])) {
      index++;
      continue;
    }
    const closing = close.get(expression[index]);
    if (closing) {
      const end = expression.indexOf(closing, index + 1);
      if (end === -1) {
        return { error: `The quote ${expression[index]} is never closed.` };
      }
      tokens.push({ quoted: true, text: expression.slice(index + 1, end) });
      index = end + 1;
      continue;
    }
    const start = index;
    while (index < expression.length && !SPACE.test(expression[index]) && !close.has(expression[index])) {
      index++;
    }
    tokens.push({ quoted: false, text: expression.slice(start, index) });
  }
  return { tokens };
}

function variableKey(word: string, spec: ConditionSpec): string | null {
  const key = word.length > 2 && word.startsWith('#') && word.endsWith('#') ? word.slice(1, -1) : word;
  const pattern = regex(spec.variablePattern);
  return pattern && pattern.test(key) ? key : null;
}

/** The server's check of a condition's shape (problemOf), in its words. */
function conditionProblem(condition: TextCondition, spec: ConditionSpec): string | null {
  if (!condition.rules.length) {
    return 'The condition has no rules.';
  }
  if (spec.maxRules && condition.rules.length > spec.maxRules) {
    return `The condition has more than ${spec.maxRules} rules.`;
  }
  const pattern = regex(spec.variablePattern);
  for (let index = 0; index < condition.rules.length; index++) {
    const rule = condition.rules[index];
    const name = `Rule ${index + 1}`;
    if (typeof rule.variable !== 'string' || (pattern && !pattern.test(rule.variable))) {
      return `${name} names no valid variable.`;
    }
    const operator = operatorById(spec, rule.operator);
    if (!operator) {
      return `${name} has an unknown operator "${rule.operator}".`;
    }
    if (!operator.needsValue) {
      if (rule.value !== undefined) {
        return `${name} (${operator.label}) takes no value.`;
      }
      continue;
    }
    if (typeof rule.value !== 'string' || !rule.value.trim()) {
      return `${name} needs a value.`;
    }
    if (spec.maxValueLength && rule.value.length > spec.maxValueLength) {
      return `${name}'s value is longer than ${spec.maxValueLength} characters.`;
    }
    if (TOKEN_IN_VALUE.test(rule.value)) {
      return `${name}'s value cannot hold a #Variable#.`;
    }
  }
  return null;
}

/**
 * Reads the words between `if` and `%}`.
 *
 * @returns the condition, or the problem in the server's words
 */
export function parseExpression(expression: string, spec: ConditionSpec): TextCondition | string {
  const keywords = spec.text.keywords;
  const { tokens, error } = expressionTokens(expression, spec);
  if (error) {
    return error;
  }
  if (!tokens.length) {
    return 'It names no variable.';
  }
  const lowerWord = (token: Token | undefined) => (token && !token.quoted ? token.text.toLowerCase() : null);
  const numberPattern = regex(spec.numberPattern);
  const shorthand = shorthandOperators(spec);
  const modes = matchIds(spec);
  // The operators by their words, the longest first, so "is not" is read before "is".
  const operatorWords = (spec.operators || [])
    .map((operator) => ({ operator, words: operator.syntax.toLowerCase().split(' ') }))
    .sort((a, b) => b.words.length - a.words.length);
  const rules: TextRule[] = [];
  let match: string | null = null;
  let index = 0;
  for (;;) {
    const negated = lowerWord(tokens[index]) === keywords.not && index + 1 < tokens.length;
    if (negated) {
      index++;
    }
    const name = tokens[index];
    const variable = name && !name.quoted ? variableKey(name.text, spec) : null;
    if (!variable) {
      return name ? `"${name.text}" is not a variable name.` : 'A variable is missing.';
    }
    index++;
    const rule: TextRule = { variable, operator: negated ? shorthand.empty : shorthand.set };
    const found = negated ? null : operatorWords.find(({ words }) => words.every(
      (word, offset) => lowerWord(tokens[index + offset]) === word));
    if (found) {
      index += found.words.length;
      rule.operator = found.operator.id;
      if (found.operator.needsValue) {
        const value = tokens[index];
        if (!value || (!value.quoted && !(numberPattern && numberPattern.test(value.text)))) {
          return `"${found.operator.syntax}" needs a value in quotes${value ? `, like "${value.text}".` : '.'}`;
        }
        rule.value = value.text;
        index++;
      }
    }
    rules.push(rule);
    if (index >= tokens.length) {
      break;
    }
    const joiner = lowerWord(tokens[index]);
    const mode = joiner === keywords.and ? modes.all : (joiner === keywords.or ? modes.any : null);
    if (!mode) {
      return negated
        ? `"${keywords.not}" goes only before a variable on its own; write "${variable} is not" `
          + 'or "does not contain" to test a value.'
        : `"${tokens[index].text}" is not a word a condition knows here.`;
    }
    if (match && match !== mode) {
      return `Use either "${keywords.and}" or "${keywords.or}" in one condition, not both.`;
    }
    match = mode;
    index++;
    if (index >= tokens.length) {
      return `Nothing follows "${joiner}".`;
    }
  }
  const condition: TextCondition = { match: match || modes.all, rules };
  const problem = conditionProblem(condition, spec);
  return problem || condition;
}

// The word a `{%` at `start` begins with, and where it ends, or null when it begins with none.
function tagWord(text: string, start: number, spec: ConditionSpec): { word: string; end: number } | null {
  let index = start + spec.text.open.length;
  while (index < text.length && SPACE.test(text[index])) {
    index++;
  }
  const word = TAG_KEYWORD.exec(text.slice(index, index + 32));
  return word ? { word: word[0], end: index + word[0].length } : null;
}

// The tag of ours that starts at `start`, or null for anybody else's. A tag that does not close
// within maxExpression characters has no end.
function readTag(text: string, start: number, spec: ConditionSpec): Tag | null {
  const syntax = spec.text;
  const keywords = syntax.keywords;
  const close = quoteClose(spec);
  const word = tagWord(text, start, spec);
  if (!word) {
    return null;
  }
  let kind = word.word.toLowerCase();
  let from = word.end;
  if (kind === keywords.else) {
    let next = from;
    while (next < text.length && SPACE.test(text[next])) {
      next++;
    }
    const following = TAG_KEYWORD.exec(text.slice(next, next + 32));
    if (following && following[0].toLowerCase() === keywords.if) {
      kind = keywords.elseif;
      from = next + following[0].length;
    }
  }
  const kinds: Record<string, TextTagKind> = {
    [keywords.if]: 'if',
    [keywords.elseif]: 'elseif',
    [keywords.else]: 'else',
    [keywords.endif]: 'endif',
  };
  if (!Object.prototype.hasOwnProperty.call(kinds, kind)) {
    return null;
  }
  const limit = Math.min(text.length, start + syntax.open.length + syntax.maxExpression + syntax.close.length);
  let quoteEnd: string | null = null;
  for (let position = from; position < limit; position++) {
    const character = text[position];
    if (quoteEnd) {
      if (character === quoteEnd) {
        quoteEnd = null;
      }
    } else if (close.has(character)) {
      quoteEnd = close.get(character);
    } else if (text.startsWith(syntax.close, position)) {
      return { kind: kinds[kind], start, end: position + syntax.close.length, expression: text.slice(from, position) };
    }
  }
  return {
    kind: kinds[kind],
    start,
    end: null,
    reason: quoteEnd
      ? `A quote in it is never closed with ${quoteEnd}.`
      : `It is not closed with ${syntax.close} within ${syntax.maxExpression} characters.`,
  };
}

// What a tag says once read: the condition of an if or elseif, or why it cannot be read.
function readTagWords(tag: Tag, spec: ConditionSpec): { condition: TextCondition | null; reason: string | null } {
  const words = decodeEntities(tag.expression).replace(ZERO_WIDTH, '').normalize('NFC');
  if (tag.kind === 'if' || tag.kind === 'elseif') {
    const parsed = parseExpression(words, spec);
    return typeof parsed === 'string' ? { condition: null, reason: parsed } : { condition: parsed, reason: null };
  }
  const keyword = tag.kind === 'else' ? spec.text.keywords.else : spec.text.keywords.endif;
  return { condition: null, reason: words.trim() ? `"${tagText(spec, keyword)}" takes nothing more.` : null };
}

function tagSource(text: string, start: number, end: number | null): string {
  const source = text.slice(start, end ?? start + 80);
  return source.length > 120 ? `${source.slice(0, 117)}...` : source;
}

// The server's parseText: a text's blocks and stray tags as a tree, in order.
function parseItems(text: string, spec: ConditionSpec, blocks: TextBlock[]): Item[] {
  const syntax = spec.text;
  const keywords = syntax.keywords;
  const root: Item[] = [];
  const open: Item[] = [];
  const here = () => (open.length ? lastOf(lastOf(open).branches).children : root);
  const errorOf = (source: string, reason: string, start: number, end: number): ItemError => ({ source, reason, start, end });
  let from = 0;
  let count = 0;
  let cut: { start: number; error: ItemError } | null = null;
  for (;;) {
    const start = text.indexOf(syntax.open, from);
    if (start === -1) {
      break;
    }
    const tag = readTag(text, start, spec);
    if (!tag) {
      // Inside a block, a misspelt branch tag ({% elsif %}, {% elif %}, {% end %}) would show as
      // text in its branch with the words after it: the block fails closed and says why.
      // Once the block has its error there is nothing more to learn, and the %} is looked for only
      // within maxExpression, as the server does, so many unclosed ones cannot make a scan quadratic.
      const word = open.length && !lastOf(open).error ? tagWord(text, start, spec) : null;
      if (word && MISSPELT_BRANCH.test(word.word)) {
        const close = text.slice(word.end, start + syntax.open.length + syntax.maxExpression).indexOf(syntax.close);
        const end = close === -1 ? null : word.end + close + syntax.close.length;
        const reason = `"${tagText(spec, word.word)}" is not a condition tag; `
          + `write ${keywords.elseif}, ${keywords.else} or ${keywords.endif}.`;
        const item = lastOf(open);
        item.error = item.error
          || errorOf(tagSource(text, start, end), reason, start, end ?? Math.min(text.length, start + 80));
      }
      from = start + syntax.open.length;
      continue;
    }
    if (++count > syntax.maxTags) {
      const end = tag.end ?? Math.min(text.length, start + 80);
      cut = { start, error: errorOf(tagSource(text, start, tag.end), `The text has more than ${syntax.maxTags} condition tags.`, start, end) };
      break;
    }
    if (tag.end === null) {
      cut = { start, error: errorOf(tagSource(text, start, null), tag.reason, start, text.length) };
      break;
    }
    from = tag.end;
    const source = tagSource(text, start, tag.end);
    const { condition, reason } = readTagWords(tag, spec);
    const error = reason ? errorOf(source, reason, start, tag.end) : null;
    const item = lastOf(open);
    const endBranch = (at: Item, to: number) => {
      lastOf(at.block.branches).contentEnd = to;
    };
    if (tag.kind === 'if') {
      const block: TextBlock = {
        start,
        end: text.length,
        depth: open.length + 1,
        branches: [{ condition, tagStart: start, tagEnd: tag.end, contentStart: tag.end, contentEnd: tag.end }],
        closed: false,
        error: null,
      };
      const next: Item = {
        start, end: null, error, block, source, sourceStart: start, sourceEnd: tag.end, hasElse: false,
        branches: [{ condition, children: [] }],
      };
      if (!next.error && block.depth > syntax.maxDepth) {
        next.error = errorOf(source, `It is nested more than ${syntax.maxDepth} deep.`, start, tag.end);
      }
      blocks.push(block);
      here().push(next);
      open.push(next);
    } else if (!item) {
      here().push({
        start, end: tag.end, error: errorOf(source, `It has no ${tagText(spec, `${keywords.if} …`)} before it.`, start, tag.end),
      });
    } else if (tag.kind === 'endif') {
      endBranch(item, start);
      item.end = tag.end;
      item.error = item.error || error;
      item.block.end = tag.end;
      item.block.closed = true;
      open.pop();
    } else {
      endBranch(item, start);
      item.error = item.error || error || (item.hasElse
        ? errorOf(source, `It comes after ${tagText(spec, keywords.else)}.`, start, tag.end) : null);
      item.hasElse = item.hasElse || tag.kind === 'else';
      const branchCondition = tag.kind === 'else' ? null : condition;
      item.branches.push({ condition: branchCondition, children: [] });
      item.block.branches.push({
        condition: branchCondition, tagStart: start, tagEnd: tag.end, contentStart: tag.end, contentEnd: tag.end,
      });
    }
  }
  // A block never closed runs to the end of the text, as does a text read no further.
  const end = cut ? cut.start : text.length;
  open.forEach((item, index) => {
    lastOf(item.block.branches).contentEnd = end;
    item.end = text.length;
    item.error = item.error || (index === 0 && cut ? cut.error
      : errorOf(item.source, `It is never closed with ${tagText(spec, keywords.endif)}.`, item.sourceStart, item.sourceEnd));
  });
  if (cut && !open.length) {
    root.push({ start: cut.start, end: text.length, error: cut.error });
  }
  return root;
}

function knownSet(known: Iterable<string> | null | undefined): Set<string> | null {
  if (!known) {
    return null;
  }
  const set = new Set<string>();
  for (const key of Array.from(known)) {
    set.add(String(key).toUpperCase());
  }
  return set;
}

/**
 * Reads every condition block of a text: where each is, its branches, and what the server's
 * validateText reports about it. Any `{% … %}` whose keyword is not one of ours is literal text,
 * except inside a block a word that starts like a branch (`{% elsif %}`, `{% end %}`): it fails the block.
 */
export function scanText(text: string, spec: ConditionSpec, options: TextScanOptions = {}): TextScan {
  const scan: TextScan = { blocks: [], problems: [], removed: [] };
  if (typeof text !== 'string' || !hasTextSyntax(spec) || !text.includes(spec.text.open)) {
    return scan;
  }
  const known = knownSet(options.knownVariables);
  const numberPattern = regex(spec.numberPattern);
  const items = parseItems(text, spec, scan.blocks);
  scan.removed = items.filter((item) => !item.block).map((item) => ({ start: item.start, end: item.end }));
  const pending = items.reverse();
  while (pending.length) {
    const item = pending.pop();
    if (item.error) {
      scan.problems.push({ message: item.error.reason, tag: item.error.source, start: item.error.start, end: item.error.end });
    }
    if (item.block) {
      item.block.error = item.error ? item.error.reason : null;
    }
    for (const branch of item.branches || []) {
      for (const rule of branch.condition ? branch.condition.rules : []) {
        const operator = operatorById(spec, rule.operator);
        const at = { tag: item.source, start: item.sourceStart, end: item.sourceEnd };
        if (operator && operator.numeric && numberPattern && !numberPattern.test(String(rule.value).trim())) {
          scan.problems.push({ message: `"${operator.syntax}": ${(spec.help && spec.help.number) || 'Enter a number.'}`, ...at });
        }
        if (known && !known.has(rule.variable.toUpperCase())) {
          scan.problems.push({ message: `#${rule.variable}# is not a variable this text can use.`, ...at });
        }
      }
    }
    for (const branch of [...(item.branches || [])].reverse()) {
      pending.push(...[...branch.children].reverse());
    }
  }
  return scan;
}

/** What would keep a text's conditions from rendering as written (the server's validateText). */
export function validateText(text: string, spec: ConditionSpec, options: TextScanOptions = {}): string[] {
  return scanText(text, spec, options).problems.map((problem) => problem.message);
}

/**
 * The innermost closed block the caret is in (strictly between its first `{%` and last `%}`), or
 * that holds the whole selection.
 */
export function blockAt(scan: TextScan, start: number, end: number = start): TextBlock | null {
  let found: TextBlock | null = null;
  for (const block of scan.blocks) {
    if (!block.closed) {
      continue;
    }
    const holds = start === end
      ? block.start < start && start < block.end
      : block.start <= start && end <= block.end;
    if (holds && (!found || block.depth > found.depth)) {
      found = block;
    }
  }
  return found;
}

/**
 * True when wrapping the selection in a new block would cut through an existing one: the
 * selection neither holds a block whole, nor sits inside one branch's text, nor misses it.
 */
export function crossesBlock(scan: TextScan, start: number, end: number): boolean {
  if (start === end) {
    return false;
  }
  return scan.blocks.some((block) => {
    if (end <= block.start || block.end <= start) {
      return false;
    }
    if (start <= block.start && block.end <= end) {
      return false;
    }
    return !block.branches.some((branch) => branch.contentStart <= start && end <= branch.contentEnd);
  });
}

/** What a block says, for the dialog; null when the render cannot read it. */
export function readBlock(text: string, block: TextBlock): TextBlockContent | null {
  if (!block || !block.closed || block.error) {
    return null;
  }
  const content: TextBlockContent = { cases: [], otherwise: null };
  for (const branch of block.branches) {
    const words = text.slice(branch.contentStart, branch.contentEnd);
    if (branch.condition) {
      content.cases.push({ condition: branch.condition, text: words });
    } else if (branch === lastOf(block.branches) && block.branches.length > 1) {
      content.otherwise = words;
    } else {
      return null;
    }
  }
  return content.cases.length ? content : null;
}

// True when the render can decide the block: closed, readable, nothing wrong with its rules.
function decidable(block: TextBlock, scan: TextScan): boolean {
  return block.closed && !block.error && !scan.problems.some((problem) => problem.start === block.start);
}

// The blocks directly between `from` and `to`, at `depth`, in order.
function blocksIn(scan: TextScan, from: number, to: number, depth: number): TextBlock[] {
  return scan.blocks.filter((block) => block.depth === depth && from <= block.start && block.end <= to);
}

// The text between `from` and `to` without the `gaps` in it.
function without(text: string, from: number, to: number, gaps: Array<{ start: number; end: number }>): string {
  let result = '';
  let cursor = from;
  [...gaps].sort((a, b) => a.start - b.start).forEach((gap) => {
    if (gap.start >= cursor) {
      result += text.slice(cursor, gap.start);
    }
    cursor = Math.max(cursor, gap.end);
  });
  return result + text.slice(Math.min(cursor, to), to);
}

/**
 * True when `holds` is true of what every recipient gets, whatever the conditions decide: of the
 * text outside every block (less what the render removes for everyone), or of each branch of a
 * block that ends in an otherwise. A block the render cannot decide never counts.
 *
 * @param scan the text's scanText
 */
export function alwaysHolds(text: string, scan: TextScan, holds: (part: string) => boolean): boolean {
  const branchHolds = (from: number, to: number, depth: number): boolean => {
    const blocks = blocksIn(scan, from, to, depth);
    if (holds(without(text, from, to, depth === 1 ? [...blocks, ...scan.removed] : blocks))) {
      return true;
    }
    return blocks.some((block) => decidable(block, scan) && block.branches.length > 1
      && lastOf(block.branches).condition === null
      && block.branches.every((branch) => branchHolds(branch.contentStart, branch.contentEnd, depth + 1)));
  };
  return branchHolds(0, text.length, 1);
}

/**
 * The longest text a recipient can get: each block replaced by its longest branch (by `measure`),
 * its tags left out. A block the render cannot decide counts as written.
 *
 * @param scan the text's scanText
 */
export function longestVariant(text: string, scan: TextScan, measure: (part: string) => number = (part) => part.length): string {
  const longest = (from: number, to: number, depth: number): string => {
    let result = '';
    let cursor = from;
    blocksIn(scan, from, to, depth).forEach((block) => {
      result += text.slice(cursor, block.start);
      cursor = block.end;
      if (!decidable(block, scan)) {
        result += text.slice(block.start, block.end);
        return;
      }
      result += block.branches
        .map((branch) => longest(branch.contentStart, branch.contentEnd, depth + 1))
        .reduce((best, variant) => (measure(variant) > measure(best) ? variant : best), '');
    });
    return result + text.slice(cursor, to);
  };
  return longest(0, text.length, 1);
}

/** The first thing wrong with one rule the dialog is building, in the ConditionEditor's words. */
export function ruleProblem(rule: TextRule, spec: ConditionSpec): string | null {
  if (!rule.variable) {
    return 'Choose a variable.';
  }
  const pattern = regex(spec.variablePattern);
  if (pattern && !pattern.test(rule.variable)) {
    return `${quote(rule.variable)} is not a variable name.`;
  }
  const operator = operatorById(spec, rule.operator);
  if (!operator) {
    return 'Choose a comparison.';
  }
  if (!operator.needsValue) {
    return null;
  }
  const value = (rule.value == null ? '' : String(rule.value)).trim();
  if (!value) {
    return 'Enter a value.';
  }
  if (spec.maxValueLength && value.length > spec.maxValueLength) {
    return `Use at most ${spec.maxValueLength} characters.`;
  }
  if (TOKEN_IN_VALUE.test(value)) {
    return 'A value cannot hold a variable such as #Name#. Compare with plain text.';
  }
  const numberPattern = regex(spec.numberPattern);
  if (operator.numeric && numberPattern && !numberPattern.test(value)) {
    return (spec.help && spec.help.number) || 'Enter a number.';
  }
  if (!quotesOf(spec).some((pair) => !value.includes(pair[0]) && !value.includes(pair[1]))) {
    return 'A value cannot hold every kind of quote mark.';
  }
  return null;
}

/** Writes a value in the first kind of quote it does not contain (as the server's buildText). */
export function quoteValue(value: string, spec: ConditionSpec): string {
  const pair = quotesOf(spec).find((quotes) => !value.includes(quotes[0]) && !value.includes(quotes[1]));
  if (!pair) {
    throw new Error('The value holds every kind of quote, so a text condition cannot hold it.');
  }
  return pair[0] + value + pair[1];
}

/** The words of a condition, e.g. `FirstName is set and Country is "NO"` (as the server writes them). */
export function formatExpression(condition: TextCondition, spec: ConditionSpec): string {
  const keywords = spec.text.keywords;
  const reserved = new Set(Object.values(keywords).map((word) => String(word).toLowerCase()));
  const numberPattern = regex(spec.numberPattern);
  return condition.rules.map((rule) => {
    const operator = operatorById(spec, rule.operator);
    const name = reserved.has(rule.variable.toLowerCase()) ? `#${rule.variable}#` : rule.variable;
    const words = `${name} ${operator ? operator.syntax : rule.operator}`;
    if (!operator || !operator.needsValue) {
      return words;
    }
    const value = String(rule.value == null ? '' : rule.value).trim();
    return `${words} ${operator.numeric && numberPattern && numberPattern.test(value) ? value : quoteValue(value, spec)}`;
  }).join(` ${condition.match === matchIds(spec).any ? keywords.or : keywords.and} `);
}

/**
 * The text of a whole block: `{% if … %}…{% elseif … %}…{% else %}…{% endif %}`. One case and its
 * otherwise come out exactly as the server's buildText writes them.
 */
export function buildText(content: TextBlockContent, spec: ConditionSpec): string {
  const keywords = spec.text.keywords;
  let result = '';
  content.cases.forEach((item, index) => {
    const keyword = index === 0 ? keywords.if : keywords.elseif;
    result += tagText(spec, `${keyword} ${formatExpression(item.condition, spec)}`) + (item.text || '');
  });
  if (content.otherwise) {
    result += tagText(spec, keywords.else) + content.otherwise;
  }
  return result + tagText(spec, keywords.endif);
}

const clip = (text: string, length: number): string => (text.length > length ? `${text.slice(0, length - 1)}…` : text);

/**
 * A condition in the dialog's words, e.g. `First name has a value`, or
 * `all of: First name has a value; Country is “NO”`.
 *
 * @param nameOf the friendly name of a simple key
 */
export function describeCondition(condition: TextCondition, spec: ConditionSpec, nameOf: (key: string) => string): string {
  const rules = condition.rules.map((rule) => {
    const operator = operatorById(spec, rule.operator);
    const words = `${nameOf(rule.variable)} ${operator ? operator.label : rule.operator}`;
    if (!operator || !operator.needsValue) {
      return words;
    }
    const value = clip(String(rule.value == null ? '' : rule.value).trim(), 40);
    return `${words} ${operator.numeric ? value : quote(value)}`;
  });
  if (rules.length === 1) {
    return rules[0];
  }
  const mode = (spec.matchModes || []).find((item) => item.id === condition.match);
  return `${mode ? mode.label : condition.match} of: ${rules.join('; ')}`;
}

/**
 * A block in plain words, e.g. `“Hi #FirstName#” shown when First name has a value. Otherwise “Hi”.`
 */
export function describeBlock(content: TextBlockContent, spec: ConditionSpec, nameOf: (key: string) => string): string {
  const shownWhen = ((spec.help && spec.help.shownWhen) || 'Shown when').toLowerCase();
  const shown = (text: string) => (text.trim() ? quote(clip(text.trim(), 60)) : 'Nothing');
  const cases = content.cases.map((item, index) => {
    const when = index === 0 ? shownWhen : 'when';
    return `${shown(item.text)} ${when} ${describeCondition(item.condition, spec, nameOf)}`;
  });
  const otherwise = content.otherwise ? ` Otherwise ${shown(content.otherwise)}.` : ' Otherwise nothing.';
  return `${cases.join('; ')}.${otherwise}`;
}
